import { Clock, Mail, MapPin, Phone, Printer } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const HOURS = [
    { days: { de: "Montag – Donnerstag", en: "Monday – Thursday" }, hours: { de: "08:00 – 12:30 & 13:30 – 17:00 Uhr", en: "08:00 – 12:30 & 13:30 – 17:00" } },
    { days: { de: "Freitag", en: "Friday" }, hours: { de: "08:00 – 14:00 Uhr", en: "08:00 – 14:00" } },
];

export default function Contact() {
    const { t } = useLang();
    return (
        <section id="kontakt" data-testid="kontakt" className="scroll-mt-24 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    <Reveal>
                        <Eyebrow>{t("Kontakt", "Contact")}</Eyebrow>
                        <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                            {t("Sprechen wir über Ihre Anlage.", "Let's talk about your system.")}
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed max-w-lg text-sm sm:text-base">
                            {t(
                                "Kein Kontaktformular, keine Warteschleife im Chat: Rufen Sie an oder schreiben Sie uns — Sie erreichen uns direkt bei Müller Satelliten- und Antennentechnik in Hemhofen.",
                                "No contact form, no chat queue: call us or drop us a line — you'll reach us directly at Müller Satelliten- und Antennentechnik in Hemhofen."
                            )}
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <a
                                href="tel:+49919594040"
                                data-testid="contact-phone-cta"
                                className="group inline-flex items-center gap-3 rounded-xl bg-red-700 hover:bg-red-800 text-white px-6 py-4 transition-colors"
                            >
                                <Phone className="h-5 w-5" />
                                <span className="flex flex-col text-left leading-tight">
                                    <span className="text-[11px] font-mono uppercase tracking-widest opacity-70">{t("Telefon", "Phone")}</span>
                                    <span className="font-semibold">+49 (0) 9195 / 9404-0</span>
                                </span>
                            </a>
                            <a
                                href="mailto:vertrieb@satmueller.de"
                                data-testid="contact-mail-cta"
                                className="group inline-flex items-center gap-3 rounded-xl bg-white border border-slate-200 hover:border-red-400 px-6 py-4 transition-colors"
                            >
                                <Mail className="h-5 w-5 text-red-700" />
                                <span className="flex flex-col text-left leading-tight">
                                    <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t("E-Mail", "Email")}</span>
                                    <span className="font-semibold text-slate-900">vertrieb@satmueller.de</span>
                                </span>
                            </a>
                        </div>
                        <div className="mt-8 space-y-3 text-sm text-slate-600">
                            <p className="flex items-center gap-2.5">
                                <MapPin className="h-4 w-4 text-red-700 shrink-0" />
                                Barthelweiher 14 · 91334 Hemhofen · Germany
                            </p>
                            <p className="flex items-center gap-2.5">
                                <Printer className="h-4 w-4 text-red-700 shrink-0" />
                                Fax +49 (0) 9195 / 9404-40
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="opening-hours-card">
                            <div className="flex items-center gap-3 border-b border-slate-200 px-6 sm:px-8 py-5">
                                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-red-50 border border-red-100 text-red-700">
                                    <Clock className="h-5 w-5" />
                                </span>
                                <h3 className="font-display text-lg font-bold text-slate-900">{t("Öffnungszeiten", "Opening hours")}</h3>
                                <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-700">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                    {t("ab Lager", "ex stock")}
                                </span>
                            </div>
                            <div className="px-6 sm:px-8 py-6 divide-y divide-slate-100">
                                {HOURS.map((h) => (
                                    <div key={h.days.de} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 py-3.5" data-testid="opening-hours-row">
                                        <span className="font-medium text-slate-900 text-sm sm:text-base">{t(h.days.de, h.days.en)}</span>
                                        <span className="font-mono text-xs sm:text-sm text-slate-600">{t(h.hours.de, h.hours.en)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-slate-50 border-t border-slate-200 px-6 sm:px-8 py-5">
                                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                                    {t(
                                        "Lieferung deutschlandweit und ins europäische Ausland. Alle Katalogartikel sind Lagerware.",
                                        "Delivery throughout Germany and across Europe. All catalogue items held in stock."
                                    )}
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
